import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, X } from "lucide-react";

/**
 * Banner shown when one or more Indian index options expire today (IST).
 * Visible to all tiers. Dismiss state stored in sessionStorage so users
 * see it on every fresh visit but not after manual dismissal in the same session.
 */
export default function ExpiryAlertBanner() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/expiry/today");
        if (alive) setData(data);
      } catch { /* silent — banner just won't render */ }
    })();
    // Refresh every hour (covers users who keep the tab open across IST midnight)
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/expiry/today");
        if (alive) setData(data);
      } catch { /* ignore */ }
    }, 60 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    if (!data) return;
    const key = `expiry-dismissed-${data.date_ist}`;
    if (sessionStorage.getItem(key)) setDismissed(true);
  }, [data]);

  if (!data || !data.expiries?.length || dismissed) return null;

  const onDismiss = () => {
    sessionStorage.setItem(`expiry-dismissed-${data.date_ist}`, "1");
    setDismissed(true);
  };

  const symbols = data.expiries.map((e) => e.symbol).join(" · ");

  return (
    <div className="border-b border-[#F59E0B]/40 bg-[#F59E0B]/10" data-testid="expiry-banner">
      <div className="px-4 md:px-6 py-2 flex items-center gap-3 text-xs">
        <AlertTriangle className="h-3.5 w-3.5 text-[#F59E0B] shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-mono uppercase tracking-widest text-[#F59E0B] font-bold mr-2">
            Expiry day · {data.weekday_ist}
          </span>
          <span className="text-zinc-200" data-testid="expiry-symbols">{symbols}</span>
          <span className="text-zinc-500 ml-2 hidden md:inline">
            options expiring today — expect higher intraday volatility & wider spreads.
          </span>
        </div>
        <button
          onClick={onDismiss}
          className="text-zinc-500 hover:text-white p-1"
          data-testid="expiry-dismiss"
          aria-label="Dismiss expiry banner"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
